import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { supabase } from '../../supabaseClient';
import { checkRateLimit, recordAttempt, formatRetryTime } from '../../utils/rateLimiter';
import { isValidEmail } from '../../utils/sanitize';

// Lockout config for login (stricter than form submission)
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes

function getLoginLockout() {
    try {
        const raw = localStorage.getItem('admin_login_lockout');
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (Date.now() < data.lockedUntil) return data;
        localStorage.removeItem('admin_login_lockout');
        return null;
    } catch {
        return null;
    }
}

function setLoginLockout(failedAttempts) {
    if (failedAttempts >= MAX_LOGIN_ATTEMPTS) {
        localStorage.setItem('admin_login_lockout', JSON.stringify({
            lockedUntil: Date.now() + LOCKOUT_DURATION_MS,
            attempts: failedAttempts,
        }));
    }
}

export default function AdminLogin() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const recaptchaRef = useRef(null);
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');

        // Check account lockout
        const lockout = getLoginLockout();
        if (lockout) {
            const remaining = lockout.lockedUntil - Date.now();
            setError(`Account locked due to too many failed attempts. Try again in ${formatRetryTime(remaining)}.`);
            return;
        }

        // Rate limit check
        const rateCheck = checkRateLimit('admin_login');
        if (!rateCheck.allowed) {
            setError(`Too many login attempts. Please try again in ${formatRetryTime(rateCheck.retryAfterMs)}.`);
            return;
        }

        // Validate email format before sending to server
        if (!isValidEmail(email)) {
            setError('Please enter a valid email address.');
            return;
        }

        // Always require CAPTCHA
        if (!recaptchaToken) {
            setError('Please complete the CAPTCHA verification.');
            return;
        }

        setLoading(true);

        // Record the attempt
        recordAttempt('admin_login');

        try {
            const { error: signInError } = await supabase.auth.signInWithPassword({
                email: email.trim().toLowerCase(),
                password,
            });

            if (signInError) {
                const newAttempts = failedAttempts + 1;
                setFailedAttempts(newAttempts);

                // Generic error message to prevent user enumeration
                setError('Invalid email or password.');

                // Set lockout if max attempts reached
                if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
                    setLoginLockout(newAttempts);
                    setError(`Too many failed attempts. Account locked for ${formatRetryTime(LOCKOUT_DURATION_MS)}.`);
                }

                // Reset CAPTCHA
                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                    setRecaptchaToken(null);
                }
            } else {
                // Login successful — clear failed attempts and redirect
                setFailedAttempts(0);
                localStorage.removeItem('admin_login_lockout');
                navigate('/admin');
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const isLockedOut = !!getLoginLockout();

    return (
        <section className="admin-login-section" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0d0010' }}>
            <div className="container" style={{ maxWidth: '500px' }}>
                <div className="card shadow-lg border-0 rounded-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="card-body p-5">
                        <h3 className="text-center text-white mb-4" style={{ fontFamily: 'var(--eventflow-font)', fontWeight: 800 }}>Admin Login</h3>
                        
                        {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}

                        {failedAttempts > 0 && failedAttempts < MAX_LOGIN_ATTEMPTS && (
                            <div className="alert alert-warning py-2" role="alert" style={{ fontSize: '13px' }}>
                                {MAX_LOGIN_ATTEMPTS - failedAttempts} attempt(s) remaining before lockout.
                            </div>
                        )}
                        
                        <form onSubmit={handleLogin} noValidate>
                            <div className="mb-3">
                                <label className="form-label" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Email</label>
                                <input 
                                    type="email" 
                                    className="form-control" 
                                    style={{ background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' }}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    maxLength={254}
                                    autoComplete="username"
                                    disabled={isLockedOut}
                                />
                            </div>
                            <div className="mb-4">
                                <label className="form-label" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Password</label>
                                <input 
                                    type="password" 
                                    className="form-control" 
                                    style={{ background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' }}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    maxLength={128}
                                    autoComplete="current-password"
                                    disabled={isLockedOut}
                                />
                            </div>

                            {/* CAPTCHA verification */}
                            <div className="mb-4 d-flex justify-content-center">
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                    onChange={val => setRecaptchaToken(val)}
                                    onExpired={() => setRecaptchaToken(null)}
                                    theme="dark"
                                />
                            </div>

                            <button 
                                type="submit" 
                                className="btn w-100 py-3 text-white fw-bold" 
                                style={{ background: 'var(--eventflow-primary)', borderRadius: '10px' }}
                                disabled={loading || isLockedOut}
                            >
                                {loading ? 'Authenticating...' : isLockedOut ? 'Account Locked' : 'Login'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}
